import {
  lookupsJson,
  homeClaimCodeToDescription,
  fpcClassesImpactingPrice,
  financialResponsibilityTiersImpactingPrice,
  getFinancialResponsibilityTier,
  getInsuranceScoreGroup,
  pullAutoDataOnlyForAutoPolicyStates,
  altCreditAlgorithmStatesDates,
  noCreditStates
} from '@ourbranch/lookups';
import uniqBy from 'lodash/uniqBy';
import mapValues from 'lodash/mapValues';
import { format } from 'date-fns';

// "20200304" to "2020-03-04"
const parseLossDate = (date) => {
  return date.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
};

const getHomeFactors = (offer) => {
  const homeFactors = [];
  const homeClaims =
    offer?.quote?.homeClaimDates && Object.keys(offer?.quote?.homeClaimDates).length !== 0
      ? JSON.parse(offer.quote.homeClaimDates)
      : {};
  const uniqueHomeClaims = mapValues(homeClaims, (claim) => uniqBy(claim, 'class'));
  Object.keys(uniqueHomeClaims).forEach((date) => {
    const claimDetails = uniqueHomeClaims[date][0];
    homeFactors.push(
      `A Home Claim on ${date} of type ${claimDetails.class}: ${
        homeClaimCodeToDescription[claimDetails.class]
      } has been identified`
    );
  });

  const uniqueHomeClaimsForUw = offer?.quote?.homeClaimsForUW
    ? uniqBy(offer?.quote?.homeClaimsForUW, (c) => c.lossTypeCode && c.lossDate)
    : [];

  uniqueHomeClaimsForUw?.forEach((claim) => {
    homeFactors.push(
      `A Home Claim on ${parseLossDate(claim.lossDate)} of type ${claim.lossTypeCode} has been identified`
    );
  });

  if (offer.quote?.home?.fpc && fpcClassesImpactingPrice.includes(offer.quote.home.fpc)) {
    homeFactors.push(
      `The Public Protection Class, which is determined by the Insurance Services Organization, for this home is ${offer.quote.home.fpc}. This code indicates how quickly and easily the local fire department will be able to address a fire at a home’s location. A higher protection class can result in an increased cost to insure a home.`
    );
  }

  const today = new Date();
  if (offer.quote?.home?.roofYear && today.getFullYear() - offer.quote.home.roofYear > 5) {
    homeFactors.push(
      `The age of this home's roof is: ${offer.quote.home.roofYear}. The age, type of materials used, and shape of a roof can have an impact on the cost to insure a home.`
    );
  }

  if (offer.quote?.home?.constructionType === 'B') {
    homeFactors.push(
      "The home's construction type is primarily Brick, as opposed to wood studs. Brick construction can cost more to repair or replace than wood frame construction."
    );
  }
  return homeFactors.length ? homeFactors : null;
};

const getPersonFactors = ({ person, includeHomeFactors, includeAutoFactors, state, rateControlDate, people }) => {
  const rateControlDateAsInt = Number(format(new Date(rateControlDate), 'yyyyMMdd'));
  const personFactors = [];
  const autoViolations =
    person.autoViolationDates && Object.keys(person.autoViolationDates).length !== 0
      ? JSON.parse(person.autoViolationDates)
      : {};
  const onlyChoosingHome = includeHomeFactors && !includeAutoFactors;
  const dontShowUDR = onlyChoosingHome && pullAutoDataOnlyForAutoPolicyStates[state];

  const uniqueAutoViolations = mapValues(autoViolations, (violation) => uniqBy(violation, 'class'));

  Object.keys(uniqueAutoViolations).forEach((violationDate) => {
    const violationsClone = [...uniqueAutoViolations[violationDate]];
    // if we have some claims-sourced violations, we only look at those:
    const claimsViolations = violationsClone.filter((v) => v.source === 'claims');
    const violations = claimsViolations.length >= 1 ? claimsViolations : violationsClone;

    // we will operate on the cloned copy to keep the original intact
    const violation = violations
      .sort((a, b) => {
        // first sorting criteria is by points
        if (a.points !== b.points) {
          return b.points - a.points;
        }
        // same points, so go alphabetically A-Z and don't worry about ties
        return a.class.toUpperCase() < b.class.toUpperCase() ? -1 : 1;
      })
      .shift();

    if (violation.class === 'UDR' && dontShowUDR) {
      return;
    }
    if (lookupsJson.autoViolationClassDescriptions[violation.class]) {
      personFactors.push(
        `A driving violation of type ${violation.class}: ${
          lookupsJson.autoViolationClassDescriptions[violation.class]
        } was identified for driver ${person.firstName} ${person.lastName} on ${violationDate}`
      );
    } else {
      personFactors.push(
        `A driving violation for driver ${person.firstName} ${person.lastName} was identified on ${violationDate} with code ${violation.class}. Please ask Branch's Insurance Product team for more details on this violation code.`
      );
    }
  });

  if (person.isPrimary && !noCreditStates[state]) {
    const autoInsuranceScore = Number(person.insuranceScore?.autoTotal || person.insuranceScore?.total);
    const homeInsuranceScore = Number(person.insuranceScore?.homeTotal || person.insuranceScore?.total);

    // Check if there's a co-applicant
    const coApplicant = people && people.find((p) => !p.isPrimary);
    const coApplicantHomeScore = Number(coApplicant?.insuranceScore?.homeTotal || coApplicant?.insuranceScore?.total);

    // Only blend if both have home scores, otherwise use primary's score
    const scoreToUse =
      coApplicantHomeScore && homeInsuranceScore ? (coApplicantHomeScore + homeInsuranceScore) / 2 : homeInsuranceScore;
    const scoreLabel = coApplicantHomeScore ? 'Blended Insurance' : 'Insurance';

    const financialResponsibilityTier =
      includeAutoFactors &&
      getFinancialResponsibilityTier(
        autoInsuranceScore,
        autoInsuranceScore,
        altCreditAlgorithmStatesDates[state] && rateControlDateAsInt >= altCreditAlgorithmStatesDates[state],
        state
      );
    const insuranceScoreGroup = includeHomeFactors && !noCreditStates[state] && getInsuranceScoreGroup(scoreToUse);
    if (!(includeHomeFactors && includeAutoFactors)) {
      if (
        financialResponsibilityTier &&
        financialResponsibilityTiersImpactingPrice.includes(financialResponsibilityTier)
      ) {
        personFactors.push(
          `Your Financial Responsibility Tier for auto insurance is ${financialResponsibilityTier}. The tier is based on a range of A1 to R1. Tier placement is based on multiple factors, including your insurance score, that are a part of your credit history and can be used to predict the likelihood of a claim. Lower tier placement can impact the cost to insure a vehicle. An insurance score is not the same thing as a credit score and they are calculated differently.`
        );
      }
      if (insuranceScoreGroup) {
        personFactors.push(
          `Your ${scoreLabel} Score Group for homeowners insurance is ${insuranceScoreGroup}/50. The insurance group is based on multiple factors that are a part of your credit history and can be used to predict the likelihood of a claim. Lower scores can impact the cost to insure a home. An insurance score is not the same thing as a credit score and they are calculated differently.`
        );
      }
    } else {
      personFactors.push(
        `Your ${scoreLabel} Score Group for homeowners insurance is ${insuranceScoreGroup}/50 and your Financial Responsibility Tier for auto insurance is ${financialResponsibilityTier}. The auto tier range is from A1 to R1 and score grouping is from 1 to 50. Insurance Score Groups and tiers are determined based on multiple factors, including your insurance score, and can be used to predict the likelihood of a claim. Lower scores or tier placement can have an impact on the price and/or eligibility of your bundled insurance. An insurance score is not the same thing as a credit score and they are calculated differently.`
      );
    }
  }

  return personFactors.length ? personFactors : null;
};

const getPersonFactorObjKey = (person) => {
  return `${person.firstName}/${person.lastName}/${person.dateOfBirth}`;
};

const getPeopleFactors = ({ people, includeHomeFactors, includeAutoFactors, state, rateControlDate }) => {
  const peopleFactors = {};

  if (people) {
    people.forEach((person) => {
      const personFactors = getPersonFactors({
        person,
        includeHomeFactors,
        includeAutoFactors,
        state,
        rateControlDate,
        people
      });
      if (personFactors) {
        const key = getPersonFactorObjKey(person);
        peopleFactors[key] = personFactors;
      }
    });
  }

  return Object.keys(peopleFactors).length ? peopleFactors : null;
};

const getCarFactors = (car) => {
  const carFactors = [];

  // mock data for dev bc severe incident data comes from carfax and we don't run carfax in dev
  // don't mock this in uat
  if (
    !window.location.host.includes('staff-uat.app') &&
    (window.location.host.includes('dev') || window.location.host.includes('localhost'))
  ) {
    car.lastSevereIncidentDate = '2020-09-14';
  }

  if (car && car.lastSevereIncidentDate) {
    carFactors.push(
      `We identified a severe incident on your ${car.make} ${car.model} ${car.year} on ${car.lastSevereIncidentDate}`
    );
  }
  return Object.keys(carFactors).length ? carFactors : null;
};

const getCarsFactors = (offer) => {
  const carsFactors = {};
  const { cars } = offer?.quote || {};

  if (cars && cars.length) {
    cars.forEach((car) => {
      const carFactors = getCarFactors(car);
      if (carFactors) {
        carsFactors[car.VIN] = carFactors;
      }
    });
  }
  return Object.keys(carsFactors).length ? carsFactors : null;
};

export const getFactorsImpactingPrice = ({ offer, homeSelected, autoSelected, homeRejected, autoRejected }) => {
  if (offer) {
    const homeFactors = getHomeFactors(offer);
    const carFactors = getCarsFactors(offer);
    const people = offer?.quote?.drivers?.length ? offer.quote.drivers : offer?.quote?.people;
    const peopleFactors = getPeopleFactors({
      people,
      includeHomeFactors: homeSelected || homeRejected,
      includeAutoFactors: autoSelected || autoRejected,
      state: offer.quote.correctedAddress.state,
      rateControlDate: offer.quote.global.rateControlDate
    });

    if (!homeFactors && !carFactors && !peopleFactors) {
      return null;
    }

    return {
      homeFactors,
      peopleFactors,
      carFactors
    };
  }
};
